import { useEffect, useRef, useState, type ReactNode } from 'react';
import { OctagonAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { isAbortError, isApiError, type WriteOptions } from '../api';
import i18n from '../i18n';
import { newIdempotencyKey } from '../lib/ids';
import { useNotifyCustomer } from './NotifyCustomer';
import { errorText } from '../lib/errorText';
import { TechnicalDetail } from './States';

/**
 * Returns `value`, or the last defined value while it is undefined (e.g. while a
 * list reloads after an action), so panels depending on it stay mounted.
 */
export function useRetained<T>(value: T | undefined): T | undefined {
  const [kept, setKept] = useState(value);
  if (value !== undefined && value !== kept) setKept(value);
  return value ?? kept;
}

type WritePhase<T> = { phase: 'idle' } | { phase: 'busy' } | { phase: 'done'; result: T } | { phase: 'error'; error: unknown };

/**
 * Runs one audited write. One Idempotency-Key per distinct request body, reused
 * when the operator retries the same body after a failure, so a lost response
 * never applies the change twice.
 */
export function useWrite<Req, Res>(send: (req: Req, opts: WriteOptions) => Promise<Res>) {
  const [state, setState] = useState<WritePhase<Res>>({ phase: 'idle' });
  const pending = useRef<{ key: string; body: string } | null>(null);
  const controller = useRef<AbortController | null>(null);
  // "Gửi hook cho khách" of the dialog this write belongs to (undefined outside one).
  const notifyCustomer = useNotifyCustomer();

  useEffect(() => () => controller.current?.abort(), []);

  const run = async (req: Req): Promise<Res | undefined> => {
    const body = JSON.stringify([req, notifyCustomer]);
    if (pending.current?.body !== body) pending.current = { key: newIdempotencyKey(), body };
    controller.current?.abort();
    const c = new AbortController();
    controller.current = c;
    setState({ phase: 'busy' });
    try {
      const result = await send(req, { idempotencyKey: pending.current.key, signal: c.signal, notifyCustomer });
      pending.current = null;
      setState({ phase: 'done', result });
      return result;
    } catch (error) {
      if (!isAbortError(error)) setState({ phase: 'error', error });
      return undefined;
    }
  };

  const reset = () => {
    controller.current?.abort();
    pending.current = null;
    setState({ phase: 'idle' });
  };

  return { state, run, reset, busy: state.phase === 'busy' };
}

/** What an operator can do about well-known error codes. */
export function errorHint(code: string): ReactNode {
  const t = i18n.t.bind(i18n);
  switch (code) {
    case 'MFA_REQUIRED':
      return (
        <>
          {t('write.mfaRequired')}{' '}
          <Link
            to={`/login?mfa=1&next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('write.mfaLink')}
          </Link>
          .
        </>
      );
    case 'FOUR_EYES_APPROVAL_REQUIRED':
      return t('write.fourEyes');
    case 'SELF_APPROVAL':
      return t('write.selfApproval');
    case 'FORBIDDEN':
      return t('write.forbidden');
    case 'IDEMPOTENCY_KEY_REUSED':
      return t('write.keyReused');
    case 'REQUEST_IN_PROGRESS':
      return t('write.inProgress');
    case 'REGISTRY_UNAVAILABLE':
      return t('write.registryUnavailable');
    case 'DOMAIN_PROTECTED':
      return t('write.domainProtected');
    case 'REGISTRANT_CONSENT_REQUIRED':
      return t('write.registrantConsentRequired');
    default:
      return null;
  }
}

/** Inline error for a failed write, surfacing the stable error code and request ID. */
export function WriteError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const hint = isApiError(error) ? errorHint(error.code) : null;
  const shown = error instanceof Error || isApiError(error) ? errorText(error) : { text: t('write.failed'), detail: null };
  return (
    <div className="flex gap-2 rounded-md border border-critical/30 bg-critical-soft p-3 text-sm text-foreground" role="alert">
      <OctagonAlert className="mt-0.5 size-4 shrink-0 text-critical" aria-hidden="true" />
      <div className="min-w-0 flex flex-col gap-1">
        <p className="break-words">
          {shown.text}
          {isApiError(error) ? (
            <>
              {' '}
              <span className="text-xs text-muted-foreground">({t('write.meta', { code: error.code, requestId: error.requestId })})</span>
            </>
          ) : null}
        </p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        <TechnicalDetail detail={shown.detail} />
      </div>
    </div>
  );
}
